import React from "react";
import { useSelector, useDispatch } from "react-redux";
import parse from "html-react-parser";

import { SimManager } from "@learningblocks/simulation-engine";

import { removeSolutionFromTube } from "./store/actions";
import styles from "./style.module.scss";

import trashIcon from "./icons/trash.svg";
import vectorIcon from "./icons/vector.svg";
import { ReactElement } from "react";
import { graphics } from "@learningblocks/simulation-engine";

interface ModalData {
  parentName?: string;
  name: string;
}

interface SimulationState {
  simulation: {
    modal: ModalData;
    tubes: Record<string, Array<Record<string, number>>>;
    tubesLimits: Record<string, Array<Record<string, number>>>;
  };
}

interface TubeLabel {
  text?: string;
}

interface TubeText {
  name: string;
}

interface TubeProps {
  label?: TubeLabel;
  text?: TubeText[];
  image?: ReactElement;
}

interface TubeDetails {
  props: TubeProps;
  imageSrc?: string;
}

const TubeContentsList = () => {
  // Helper to format volume in µL or mL
  const formatVolume = (val: number) => {
    if (val >= 1000) {
      return `${(val / 1000).toFixed(2)} mL`;
    }
    return `${val.toFixed(2)} µL`;
  };
  const dispatch = useDispatch();
  const modalData = useSelector(
    (state: SimulationState) => state.simulation.modal
  );
  const tubeID = modalData?.parentName;
  const solutions = useSelector((state: SimulationState) =>
    tubeID ? state.simulation.tubes[tubeID] : undefined
  );
  const solutionsLimits = useSelector((state: SimulationState) =>
    tubeID ? state.simulation.tubesLimits[tubeID] : undefined
  );
  const tubeDetails = tubeID
    ? (SimManager.getInstance().getObject(tubeID) as TubeDetails | null)
    : null;

  // Check if tubeID, tubeDetails, and modalData are valid before rendering
  if (!tubeID || !tubeDetails || modalData.name !== "InspectTubeModal") {
    return <></>;
  }

  const getObjectTitle = (id: string) => {
    const obj = SimManager.getInstance().getObject(id) as TubeDetails | null;
    return parse(obj?.props.label?.text ?? id);
  };

  const imageName = tubeDetails.imageSrc;

  const imageSrc = imageName
    ? graphics.getInstance().getImage(imageName)
    : undefined;

  const totalVolume = (solutions ?? []).reduce(
    (acc: number, sol: Record<string, number>) =>
      acc + Object.values(sol).reduce((a: number, v: number) => a + v, 0),
    0
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.tubeContentsList}>
        <div className={styles.tubeContentsListInner}>
          <div className={styles.frameWrapper}>
            <div className={styles.solutionDParent}>
              <div className={styles.solutionD}>
                {tubeDetails.props.label?.text ? (
                  <>{parse(tubeDetails.props.label?.text ?? "")} solution</>
                ) : (
                  <>{tubeID} solution</>
                )}
                <p>Total Volume: {formatVolume(totalVolume)}</p>
              </div>
              {solutions && solutions.length > 0 ? (
                <div className={styles.instanceParent}>
                  {solutions.map(
                    (solution: Record<string, number>, idx: number) =>
                      Object.entries(solution).map(([title, volume]) => (
                        <div
                          className={styles.frameContainer}
                          key={`${title}-${idx}`}
                        >
                          <div className={styles.frameTextWrapper}>
                            <div className={styles.frameText}>
                              {formatVolume(volume)} {getObjectTitle(title)}
                              {(() => {
                                if (
                                  solutionsLimits &&
                                  Array.isArray(solutionsLimits)
                                ) {
                                  const limitObj = solutionsLimits.find(
                                    (limit: Record<string, number>) =>
                                      limit[title] !== undefined
                                  );
                                  if (
                                    limitObj &&
                                    limitObj[title] !== undefined
                                  ) {
                                    if (volume === limitObj[title]) {
                                      return null;
                                    }
                                    return (
                                      <p className={styles.errorMessage}>
                                        This should be{" "}
                                        {formatVolume(limitObj[title])}{" "}
                                        {getObjectTitle(title)}
                                      </p>
                                    );
                                  } else {
                                    return (
                                      <p className={styles.errorMessage}>
                                        {
                                          "This should not be added in this tube"
                                        }
                                      </p>
                                    );
                                  }
                                }
                                return (
                                  <p className={styles.errorMessage}>
                                    {"This should not be added in this tube"}
                                  </p>
                                );
                              })()}
                            </div>
                          </div>
                          {!(
                            solutionsLimits &&
                            solutionsLimits.some &&
                            solutionsLimits.some(
                              (limit: Record<string, number>) =>
                                limit[title] === volume
                            )
                          ) && (
                            <div
                              role="button"
                              tabIndex={0}
                              className={styles.trashIconWrapper}
                              onClick={() =>
                                dispatch(
                                  removeSolutionFromTube({
                                    id: tubeID,
                                    solution: title,
                                  })
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  dispatch(
                                    removeSolutionFromTube({
                                      id: tubeID,
                                      solution: title,
                                    })
                                  );
                                }
                              }}
                            >
                              <img
                                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                                className={styles.trashIcon}
                                alt="Remove"
                                src={trashIcon}
                              />
                            </div>
                          )}
                        </div>
                      ))
                  )}
                </div>
              ) : (
                <div className={styles.instanceParent}>
                  <div className={styles.emptyStateWrapper}>
                    <div className={styles.frameText}>No solution in tube</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <img className={styles.tubeContentsListChild} alt="" src={vectorIcon} />
      </div>
      <div>
        {tubeDetails.props.text && tubeDetails.props.text.length > 0 && (
          <span
            className={`${styles[`${tubeDetails.props.text[0].name}Tube`]} ${styles.tubeNameText}`}
          >
            {tubeDetails.props.text[0].name}
          </span>
        )}
        {tubeDetails.props.image &&
          React.cloneElement(tubeDetails.props.image, {
            style: { width: 139 },
            src: imageSrc,
          })}
      </div>
    </div>
  );
};

export default TubeContentsList;
